import { Space } from '@sravni/react-design-system';
import { SeoBlock } from '@sravni/react-seo';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';
import { ErrorBoundary } from 'react-error-boundary';

import { sendSentryClientErrorOnce } from 'shared/lib';
import { useAppSelector } from 'shared/lib/redux';

import { seoContentBlocksSelector, SeoExpertsBlock } from 'entities/metadata';
import { decorateReviews, reviewsItemsSelector, ReviewsBlock } from 'entities/reviews';
import { TopOffersColumn, TopOffersRow, topOffersSelector } from 'entities/vitrinsTopOffers';

const SeoBlockBoundary: FC<{ id: string }> = ({ children, id }) => (
  <ErrorBoundary
    fallbackRender={() => null}
    onError={(error: Error, info: { componentStack: string }) =>
      sendSentryClientErrorOnce(id, error, { componentStack: info.componentStack, SeoBlockId: id }, { level: 'fatal' })
    }
  >
    {children}
  </ErrorBoundary>
);

export const SeoBlocks: FC = ({ className }) => {
  const seoContentBlocks = useAppSelector(seoContentBlocksSelector);
  const reviews = useAppSelector(reviewsItemsSelector);
  const topOffers = useAppSelector(topOffersSelector);

  if (!seoContentBlocks) {
    return null;
  }

  return (
    <Space
      direction="vertical"
      size={16}
    >
      {seoContentBlocks.map((seoContentBlock) => {
        switch (seoContentBlock.type) {
          case 'html': {
            return (
              <SeoBlockBoundary
                id="SeoBlocksHtml"
                key={seoContentBlock.type}
              >
                <SeoBlock
                  className={className}
                  contentBlocks={[
                    {
                      type: seoContentBlock.type,
                      text: seoContentBlock.text,
                      author: seoContentBlock.author,
                    },
                  ]}
                />
              </SeoBlockBoundary>
            );
          }
          case 'topOffers': {
            if (!topOffers) {
              return null;
            }
            return (
              <SeoBlockBoundary
                id="SeoBlocksTopOffers"
                key={seoContentBlock.type}
              >
                <SeoBlock
                  className={className}
                  contentBlocks={[
                    {
                      type: seoContentBlock.type,
                      title: seoContentBlock.title,
                      subTitle: seoContentBlock.subTitle,
                      headers: topOffers.headers,
                      rows: topOffers.rows?.map((row, index) => {
                        const [product, ...columns] = row;

                        // eslint-disable-next-line react/no-array-index-key
                        return [
                          <TopOffersRow
                            defaultLink={`/bank/${(product as unknown as IProduct).organization.alias}/kredity/`}
                            product={product as unknown as IProduct}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`top-offer-${index}`}
                          />,
                          ...columns.map((item, itemIndex) => (
                            <TopOffersColumn
                              item={item}
                              // eslint-disable-next-line react/no-array-index-key
                              key={`top-offer-column-item-${itemIndex}`}
                            />
                          )),
                        ];
                      }),
                    },
                  ]}
                />
              </SeoBlockBoundary>
            );
          }
          case 'editorialBoard': {
            return (
              <SeoBlockBoundary
                id="SeoBlocksEditorialBoard"
                key={seoContentBlock.type}
              >
                <SeoExpertsBlock
                  block={seoContentBlock}
                  className={className}
                />
              </SeoBlockBoundary>
            );
          }
          case 'faq': {
            return (
              <SeoBlockBoundary
                id="SeoBlocksFaq"
                key={seoContentBlock.type}
              >
                <SeoBlock
                  className={className}
                  contentBlocks={[
                    {
                      type: seoContentBlock.type,
                      title: seoContentBlock.serviceResponseData.title,
                      items: seoContentBlock.serviceResponseData.items,
                    },
                  ]}
                />
              </SeoBlockBoundary>
            );
          }
          case 'expertOpinion': {
            if (!seoContentBlock?.answers?.length) {
              return null;
            }

            return (
              <SeoBlockBoundary
                id="SeoBlocksExpertOpinion"
                key={seoContentBlock.type}
              >
                <SeoBlock
                  className={className}
                  contentBlocks={[
                    {
                      type: seoContentBlock.type,
                      title: seoContentBlock.title,
                      answersData: seoContentBlock.answersData.map((data) => ({
                        ...data,
                        link: `/u/${data.expertId}/`,
                      })),
                    },
                  ]}
                />
              </SeoBlockBoundary>
            );
          }
          case 'review': {
            return (
              <SeoBlockBoundary
                id="SeoBlocksReview"
                key={seoContentBlock.type}
              >
                <ReviewsBlock
                  heading={seoContentBlock.title}
                  reviews={decorateReviews(reviews)}
                />
              </SeoBlockBoundary>
            );
          }
          default: {
            return null;
          }
        }
      })}
    </Space>
  );
};
